import React, { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabaseClient'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { fromZonedTime } from 'date-fns-tz'
import {sanity} from '../lib/sanity'

import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import CustomCheckbox from '@/components/ui/checkbox_custom'

/* const eventTypes = [
  'Film',
  'Herdenking',
  'Festival',
  'Lezing',
  'Kennisdeling',
  'Tentoonstelling',
  'Muziek',
  'Workshops',
  'Debat',
  'Dialoog',
  'Interactief',
  'Boekpresentatie',
  'Presentatie',
  'Voorstelling',
  'Mode',
  'Onderwijs',
  'Kinderen',
  'Bijeenkomst',
  'Anders',
]

const filterDates = [
  'Herdenking - 29 juni',
  'Herdenking - 30 juni',
  'Nationale Herdenking - 1 juli',
  'Dia di Tula - 17 aug',
  'Dag der Inheemse Volken - 9 aug',
  'Andere gelegenheid / datum',
]

const regions = [
  'Nederland',
  'Suriname',
  'Aruba',
  'Bonaire',
  'Curaçao',
  'Saba',
  'Sint Eustatius',
  'Sint Maarten',
]

const regionTimeZones = {
  Nederland: 'Europe/Amsterdam',
  Suriname: 'America/Paramaribo',
  Aruba: 'America/Aruba',
  Bonaire: 'America/Kralendijk',
  Curaçao: 'America/Curacao',
  Saba: 'America/Kralendijk',
  'Sint Eustatius': 'America/Kralendijk',
  'Sint Maarten': 'America/Lower_Princes',
}
*/
const ticketOptions = [
  'Deelname is gratis. Aanmelden is vereist.',
  'Deelname is gratis. Aanmelden wordt gewaardeerd.',
  'Deelname is gratis. Aanmelden is niet nodig.',
  'Voor deelname is een ticket nodig. Bestel deze online.',
  'Voor deelname is een ticket nodig. Koop deze op locatie.',
  'Voor deelname is een ticket nodig. Bestel deze online of aan de deur.',
] 

const EVENT_FILTER_OPTIONS_QUERY = `*[
  _type == "eventFilterOption" &&
  coalesce(active, true) == true
] | order(kind asc, title asc) {
  _id,
  kind,
  title,
  value,
  timeZone
}`

const formSchema = z.object({
  contactName: z.string().min(1, 'Vul een contactpersoon in'),
  contactEmail: z.string().email('Vul een geldig e-mailadres in'),
  contactOrganisation: z.string().min(1, 'Vul een organisatie in'),

  title: z.string().min(1, 'Vul een titel in'),
  organiser: z.string().min(1, 'Vul een organisator in'),
  type: z.string().min(1, 'Kies een type'),
  date: z.string().min(1, 'Kies een filterdatum'),

  startDate: z.string().min(1, 'Kies een startdatum'),
  startTime: z.string().min(1, 'Kies een starttijd'),
  endDate: z.string().optional(),
  endTime: z.string().optional(),

  venueName: z.string().optional(),
  streetName: z.string().optional(),
  streetNumber: z.string().optional(),
  postalCode: z.string().optional(),
  city: z.string().min(1, 'Vul een plaats in'),
  region: z.string().min(1, 'Kies een regio'),

  description: z.string().min(1, 'Vul een beschrijving in'),
  fundedByHerdenkingscomite: z.boolean().default(false),
  ticketText: z.string().min(1, 'Kies ticketinformatie'),
  ticketUrl: z
    .string()
    .optional()
    .or(z.literal(''))
    .transform((value) => {
      if (!value) return ''

      if (value.startsWith('http://') || value.startsWith('https://')) {
        return value
      }

      return `https://${value}`
    })
    .pipe(z.string().url('Vul een geldige URL in').optional().or(z.literal(''))),

  terms: z.literal(true, {
    errorMap: () => ({ message: 'U moet akkoord gaan om door te gaan' }),
  }),
})

function combineDateAndTime(date, time, timeZone) {
  if (!date || !time || !timeZone) return null

  const localDateTime = `${date} ${time}:00`

  return fromZonedTime(localDateTime, timeZone).toISOString()
}

function slugify(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}

function Request() {
  const [submissionStatus, setSubmissionStatus] = useState(null)
  const [imageFile, setImageFile] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [filterOptions, setFilterOptions] = useState([])

  const eventTypes = filterOptions.filter((option) => option.kind === 'type')
const filterDates = filterOptions.filter((option) => option.kind === 'date')
const regions = filterOptions.filter((option) => option.kind === 'location')

const regionTimeZones = Object.fromEntries(
  regions.map((region) => [
    region.value,
    region.timeZone || 'Europe/Amsterdam',
  ])
)

useEffect(() => {
  window.scrollTo(0, 0)

  sanity
    .fetch(EVENT_FILTER_OPTIONS_QUERY)
    .then((result) => {
      setFilterOptions(result || [])
    })
    .catch((error) => {
      console.error(error)
    })
}, [])

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      contactName: '',
      contactEmail: '',
      contactOrganisation: '',

      title: '',
      organiser: '',
      type: '',
      date: '',

      startDate: '',
      startTime: '',
      endDate: '',
      endTime: '',

      venueName: '',
      streetName: '',
      streetNumber: '',
      postalCode: '',
      city: '',
      region: '',

      description: '',
      fundedByHerdenkingscomite: false,
      ticketText: '',
      ticketUrl: '',

      terms: false,
    },
  })

  const contactOrganisation = form.watch('contactOrganisation')
  const organiser = form.watch('organiser')

  useEffect(() => {
    if (!organiser && contactOrganisation) {
      form.setValue('organiser', contactOrganisation)
    }
  }, [contactOrganisation, organiser, form])

  function normalizeUrl(url) {
    if (!url) return ''

    if (
      url.startsWith('http://') ||
      url.startsWith('https://')
    ) {
      return url
    }

    return `https://${url}`
  }

  const onSubmit = async (data) => {
    setIsSubmitting(true)
    setSubmissionStatus(null)

    try {
      let imageUrl = null

      if (imageFile) {
        const fileExt = imageFile.name.split('.').pop()
        const fileName = `${Date.now()}-${slugify(data.title)}.${fileExt}`
        const filePath = `event-images/${fileName}`

        const { error: uploadError } = await supabase.storage
          .from('event-submissions')
          .upload(filePath, imageFile)

        if (uploadError) throw uploadError

        const { data: publicUrlData } = supabase.storage
          .from('event-submissions')
          .getPublicUrl(filePath)

        imageUrl = publicUrlData.publicUrl
      }

      const timeZone = regionTimeZones[data.region] || 'Europe/Amsterdam'

      const startDateTime = combineDateAndTime(
        data.startDate,
        data.startTime,
        timeZone
      )
      
      const endDateTime =
        data.endDate && data.endTime
          ? combineDateAndTime(data.endDate, data.endTime, timeZone)
          : null

      const submissionId = crypto.randomUUID()

      const { error } = await supabase.from('event_submissions').insert([
        {
          id: submissionId,
          status: 'new',

          contact_name: data.contactName,
          contact_email: data.contactEmail,
          contact_organisation: data.contactOrganisation,

          title: data.title,
          slug: slugify(data.title),
          organiser: data.organiser,
          type: data.type,
          date: data.date,

          start_datetime: startDateTime,
          end_datetime: endDateTime,

          venue_name: data.venueName || null,
          street_name: data.streetName || null,
          street_number: data.streetNumber || null,
          postal_code: data.postalCode || null,
          city: data.city,
          region: data.region,
          time_zone: timeZone,

          description: data.description,
          funded_by_herdenkingscomite: data.fundedByHerdenkingscomite,
          ticket_text: data.ticketText,
          ticket_url: data.ticketUrl || null,

          image_url: imageUrl,
          terms: data.terms,
        },
      ])

      if (error) throw error

      const {error: functionError} = await supabase.functions.invoke(
        'create-sanity-event-draft',
        {
          body: {
            submissionId,
          },
        }
      )
      
      if (functionError) {
        console.error(functionError)
      }

      setSubmissionStatus({
        type: 'success',
        message: 'Bedankt! Het evenement is succesvol ingestuurd.',
      })

      form.reset()
      setImageFile(null)
    } catch (error) {
      console.error(error)
      setSubmissionStatus({
        type: 'error',
        message: 'Er ging iets mis. Probeer het opnieuw.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <main className="min-h-screen text-[#5757fd]">
      <div className="mx-auto max-w-prose px-8 my-24 xl:my-36">
        <div className="my-16">
          <h1 className="text-4xl font-sans_bold mb-8">Deel je evenement</h1>
          <p className="text-lg font-sans leading-tight">
            Organiseer je een herdenking, viering, programma of activiteit rond
            het slavernijverleden? Vul dit formulier in. Na controle kunnen we
            het evenement toevoegen aan de agenda.
          </p>
        </div>

        <section>
          <h2 className="text-3xl font-sans mb-8">Aanmeldformulier</h2>

          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8 text-black font-sans">
              <div className="rounded-3xl bg-white/40 p-6 space-y-4">
                <h3 className="text-xl font-sans_bold">Contactgegevens</h3>

                <FormField
                  control={form.control}
                  name="contactName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contactpersoon*</FormLabel>
                      <FormControl>
                        <Input className="placeholder:text-black/30" placeholder="Naam contactpersoon" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="contactEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>E-mail*</FormLabel>
                      <FormControl>
                        <Input type="email" className="placeholder:text-black/30" placeholder="naam@organisatie.nl" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="contactOrganisation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Organisatie*</FormLabel>
                      <FormControl>
                        <Input className="placeholder:text-black/30" placeholder="Naam organisatie" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="rounded-3xl bg-white/40 p-6 space-y-4">
                <h3 className="text-xl font-sans_bold">Evenement</h3>

                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Titel evenement*</FormLabel>
                      <FormControl>
                        <Input className="placeholder:text-black/30" placeholder="Bijvoorbeeld: Nationale Herdenking Slavernijverleden" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="organiser"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Organisatie(s)*</FormLabel>
                      <FormControl>
                        <Input className="placeholder:text-black/30" placeholder="Organisatie(s)" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type*</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className='font-sans text-black'>
                            <SelectValue placeholder="Kies een type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                        {eventTypes.map((type) => (
  <SelectItem key={type.value} value={type.value}>
    {type.title}
  </SelectItem>
))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Filterdatum*</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className='font-sans text-black'>
                            <SelectValue placeholder="Kies een datumcategorie" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                        {filterDates.map((date) => (
  <SelectItem key={date.value} value={date.value}>
    {date.title}
  </SelectItem>
))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Startdatum*</FormLabel>
                        <FormControl>
                          <Input className='font-sans text-black' type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="startTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Aanvang*</FormLabel>
                        <FormControl>
                          <Input className='font-sans text-black' type="time" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="endDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Einddatum</FormLabel>
                        <FormControl>
                          <Input className='font-sans text-black' type="date" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="endTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Eindtijd</FormLabel>
                        <FormControl>
                          <Input className='font-sans text-black' type="time" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Beschrijving*</FormLabel>
                      <p className='text-sm text-black/60 pb-4'>Deze beschrijving wordt gepubliceerd op de website om bezoekers te informeren over het evenement.</p>
                      <FormControl>
                        <Textarea
                          rows={8}
                          className="placeholder:text-black/30"
                          placeholder="Beschrijf het evenement..."
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

<FormField
  control={form.control}
  name="fundedByHerdenkingscomite"
  render={({field}) => (
    <FormItem className="rounded-2xl bg-background  p-4">
      <div className="flex items-start gap-3">
        <FormControl>
          <CustomCheckbox
            checked={field.value}
            onChange={field.onChange}
          />
        </FormControl>

        <div>
          <FormLabel className="text-black leading-tight">
            Heeft jouw evenement financiering ontvangen van het
            Herdenkingscomité Slavernijverleden?
          </FormLabel>

        </div>
      </div>

      <FormMessage />
    </FormItem>
  )}
/>
              </div>

              <div className="rounded-3xl bg-white/40 p-6 space-y-4">
                <h3 className="text-xl font-sans_bold">Locatie</h3>

                <FormField
                  control={form.control}
                  name="venueName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Naam locatie</FormLabel>
                      <FormControl>
                        <Input className="placeholder:text-black/30" placeholder="Bijvoorbeeld: Oosterpark" {...field} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <div className="grid gap-4 md:grid-cols-[1fr_0.35fr]">
                  <FormField
                    control={form.control}
                    name="streetName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Straatnaam</FormLabel>
                        <FormControl>
                          <Input className="placeholder:text-black/30" placeholder="Straatnaam" {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="streetNumber"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Huisnummer</FormLabel>
                        <FormControl>
                          <Input className="placeholder:text-black/30" placeholder="12" {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-[0.5fr_1fr]">
                  <FormField
                    control={form.control}
                    name="postalCode"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Postcode</FormLabel>
                        <FormControl>
                          <Input className="placeholder:text-black/30" placeholder="1234 AB" {...field} />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Plaats*</FormLabel>
                        <FormControl>
                          <Input className="placeholder:text-black/30" placeholder="Plaatsnaam" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="region"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Regio / land waar je evenement plaatsvindt*</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className='font-sans text-black'>
                            <SelectValue placeholder="Kies een regio" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                        {regions.map((region) => (
  <SelectItem key={region.value} value={region.value}>
    {region.title}
  </SelectItem>
))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="rounded-3xl bg-white/40 p-6 space-y-4">
                <h3 className="text-xl font-sans_bold">Tickets en afbeelding</h3>

                <FormField
                  control={form.control}
                  name="ticketText"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ticketinformatie*</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger className='font-sans text-black/65'>
                            <SelectValue className="placeholder:text-black/30" placeholder="Kies ticketinformatie" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {ticketOptions.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="ticketUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Ticket- of aanmeldlink</FormLabel>
                      <FormControl>
                        <Input className="placeholder:text-black/30" placeholder="https://..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div>
                  <FormLabel>Afbeelding</FormLabel>
                  <Input
                    className="text-black/30"
                    type="file"
                    accept=".jpg,.jpeg,.png"
                    onChange={async (event) => {
                      const file = event.target.files?.[0]

                      if (!file) return

                      // Allowed types
                      const allowedTypes = [
                        'image/jpeg',
                        'image/jpg',
                        'image/png',
                      ]

                      if (!allowedTypes.includes(file.type)) {
                        alert('Upload alleen JPG of PNG bestanden.')
                        event.target.value = ''
                        return
                      }

                      // Max size: 8MB
                      const maxSize = 8 * 1024 * 1024

                      if (file.size > maxSize) {
                        alert('Afbeelding mag maximaal 8MB zijn.')
                        event.target.value = ''
                        return
                      }

                      // Check dimensions
                      const image = new Image()
                      const imageUrl = URL.createObjectURL(file)

                      image.onload = () => {
                        const width = image.width
                        const height = image.height

                        // Minimum resolution
                        if (width < 1200 || height < 800) {
                          alert(
                            'Afbeelding is te klein. Minimum formaat is 1200×800 pixels.'
                          )
                          event.target.value = ''
                          return
                        }

                        // Maximum resolution
                        if (width > 8000 || height > 8000) {
                          alert(
                            'Afbeelding is te groot. Maximum formaat is 8000×8000 pixels.'
                          )
                          event.target.value = ''
                          return
                        }

                        setImageFile(file)
                      }

                      image.src = imageUrl
                    }}
                  />
                  <p className="mt-2 text-sm font-sans text-black/60">
                    Upload bij voorkeur een liggende afbeelding.
                  </p>
                </div>
              </div>

              <FormField
                control={form.control}
                name="terms"
                render={({ field }) => (
                  <FormItem className="flex flex-col pt-4">
                    <div className="flex items-start gap-3">
                      <FormControl>
                        <CustomCheckbox
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      </FormControl>
                      <FormLabel className="text-[#5757fd] leading-tight">
                        Ik geef toestemming om deze gegevens te gebruiken voor de
                        beoordeling en publicatie van dit evenement.
                      </FormLabel>
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full rounded-full bg-[#5757fd] py-6 text-white hover:bg-[#4444e8]"
              >
                {isSubmitting ? 'Versturen...' : 'Evenement insturen'}
              </Button>
            </form>
          </Form>

          {submissionStatus && (
            <div
              className={`my-6 rounded-2xl px-4 py-3 text-center font-sans text-sm ${submissionStatus.type === 'success'
                  ? 'bg-green-100 text-green-700'
                  : 'bg-red-100 text-red-700'
                }`}
            >
              {submissionStatus.message}
            </div>
          )}
        </section>
      </div>
    </main>
  )
}

export default Request
